import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Headphones, Loader2, Radio } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { autopilotApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { fmtPhone } from '@/lib/format';
import { useCan } from '@/features/auth/store';

/**
 * Faixa abaixo do cabeçalho: mostra que o piloto está ligado e, no canal navegador, entrega
 * a próxima ligação pronta com um clique. Uma coisa que disca sozinha não pode ser invisível.
 */
export function AutopilotBanner() {
  const canSee = useCan('campaigns.read');
  const canCall = useCan('leads.call');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['autopilot'],
    queryFn: autopilotApi.status,
    enabled: canSee,
    refetchInterval: 10_000,
  });
  const answer = useMutation({
    mutationFn: autopilotApi.webCall,
    onSuccess: ({ call, accessToken }) => {
      void qc.invalidateQueries({ queryKey: ['autopilot'] });
      void qc.invalidateQueries({ queryKey: ['calls'] });
      navigate(`/chamadas/${call.id}`, { state: { accessToken } });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Não foi possível atender'),
  });

  const st = q.data;
  if (!st?.enabled) return null;
  const next = st.channel === 'web' ? st.pendingWeb[0] : undefined;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b bg-muted/40 px-4 py-2 text-sm">
      <span className="inline-flex items-center gap-1.5 font-medium">
        <Radio className="size-4 animate-pulse text-success" /> Piloto automático ligado
      </span>
      <span className="text-muted-foreground">
        {st.channel === 'web' ? 'canal navegador (teste)' : 'canal telefone'} · {st.leadsPerRun} por
        ciclo a cada {st.intervalMin} min
        {st.campaign && (
          <>
            {' · '}
            <Link className="underline" to={`/campanhas/${st.campaign.id}`}>
              {st.campaign.completedLeads}/{st.campaign.totalLeads} ligados
            </Link>
          </>
        )}
      </span>
      {next && (
        <span className="ml-auto inline-flex items-center gap-2">
          <span>
            Ligação pronta para <strong>{next.leadName || 'lead sem nome'}</strong>{' '}
            <span className="text-muted-foreground tabular-nums">({fmtPhone(next.phoneE164)})</span>
            {next.productName && (
              <span className="text-muted-foreground"> · {next.productName}</span>
            )}
            {st.pendingWeb.length > 1 && (
              <span className="text-muted-foreground"> · +{st.pendingWeb.length - 1} na fila</span>
            )}
          </span>
          {canCall && (
            <Button
              size="sm"
              onClick={() => answer.mutate(next.campaignLeadId)}
              disabled={answer.isPending}
            >
              {answer.isPending ? <Loader2 className="animate-spin" /> : <Headphones />} Atender
              pelo navegador
            </Button>
          )}
        </span>
      )}
    </div>
  );
}
